import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { ArrowLeft, Lock, Play, FileText, Presentation, ChevronDown, ChevronUp } from "lucide-react";

// Convert any YouTube URL to an embeddable form
function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function SeriesDetail() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/members/series/${slug}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.status === 403 ? "membership" : "notfound"));
  }, [user, slug]);

  if (loading) return <div className="px-6 py-32 text-center" style={{ color: "var(--muted)" }}>Loading…</div>;
  if (!user) return <Navigate to={`/login?next=/dashboard/series/${slug}`} replace />;

  if (err === "membership" || (!loading && !user.membership_active && !user.is_admin)) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="series-paywall">
        <Lock size={40} strokeWidth={1.5} style={{ color: "var(--ember)", margin: "0 auto" }} />
        <h1 className="font-serif-display text-4xl mt-6" style={{ color: "var(--ink)" }}>Members only</h1>
        <Link to="/membership" className="btn-primary mt-8 inline-flex">Become a member</Link>
      </div>
    );
  }

  if (!data) return <div className="px-6 py-32 text-center" style={{ color: "var(--muted)" }}>Loading series…</div>;

  const { series, lessons } = data;

  return (
    <div data-testid="series-detail-page" className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-10" style={{ color: "var(--ink-2)" }}>
        <ArrowLeft size={14} /> Back to members hub
      </Link>

      <div className="overline mb-4">Study Series</div>
      <h1 className="font-serif-display tracking-[-0.045em] leading-[0.88]" style={{ color: "var(--ink)" }}>
        <span className="block text-5xl sm:text-6xl lg:text-8xl">{series.name}<span style={{ color: "var(--ember)" }}>.</span></span>
      </h1>
      <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
        {series.description}
      </p>

      <div className="mt-8 flex items-center gap-6 text-sm" style={{ color: "var(--ink-2)" }}>
        <div>
          <div className="overline" style={{ fontSize: "0.65rem" }}>Lessons available</div>
          <div className="font-serif-display text-3xl mt-1" style={{ color: "var(--ink)" }}>
            {lessons.length}
            {series.target_lessons > 0 && <span className="text-base" style={{ color: "var(--muted)" }}> / {series.target_lessons}</span>}
          </div>
        </div>
        {series.target_lessons > 0 && (
          <div className="flex-1 max-w-md">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--surface)" }}>
              <div className="h-full" style={{
                width: `${Math.min(100, (lessons.length / series.target_lessons) * 100)}%`,
                background: "var(--ember)"
              }} />
            </div>
          </div>
        )}
      </div>

      <div className="editorial-rule my-12" />

      {lessons.length === 0 ? (
        <p style={{ color: "var(--muted)" }}>No lessons published in this series yet — check back soon.</p>
      ) : (
        <div className="space-y-4">
          {lessons.map((l) => {
            const open = openId === l.id;
            const embed = ytEmbed(l.youtube_url);
            return (
              <article key={l.id} data-testid={`lesson-card-${l.id}`} className="card-surface overflow-hidden">
                <button
                  onClick={() => setOpenId(open ? null : l.id)}
                  data-testid={`lesson-toggle-${l.id}`}
                  className="w-full text-left p-6 lg:p-8 flex items-start gap-6 transition-colors"
                >
                  <div className="font-serif-display text-4xl lg:text-5xl shrink-0" style={{ color: "var(--ember)" }}>
                    {String(l.lesson_number).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif-display text-2xl lg:text-3xl leading-tight" style={{ color: "var(--ink)" }}>{l.title}</h3>
                    <p className="mt-2 text-sm md:text-base" style={{ color: "var(--ink-2)" }}>{l.summary}</p>
                  </div>
                  <div className="shrink-0 self-center" style={{ color: "var(--ember)" }}>
                    {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </button>

                {open && (
                  <div className="px-6 lg:px-8 pb-8 pt-2 border-t" style={{ borderColor: "var(--border-subtle)" }}>
                    {embed && (
                      <div className="aspect-video w-full mb-6 mt-4">
                        <iframe
                          src={embed}
                          title={l.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                          data-testid={`lesson-${l.id}-youtube`}
                        />
                      </div>
                    )}

                    {l.body && (
                      <div className="text-base leading-[1.75] whitespace-pre-line mb-6" style={{ color: "var(--ink-2)" }}>
                        {l.body}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                      {l.youtube_url && (
                        <a href={l.youtube_url} target="_blank" rel="noopener noreferrer" data-testid={`lesson-${l.id}-yt-link`} className="btn-ghost">
                          <Play size={14} /> Open on YouTube
                        </a>
                      )}
                      {l.slides_url && (
                        <a href={l.slides_url} target="_blank" rel="noopener noreferrer" data-testid={`lesson-${l.id}-slides-link`} className="btn-ghost">
                          <Presentation size={14} /> Slides
                        </a>
                      )}
                      {l.student_sheet_url && (
                        <a href={l.student_sheet_url} target="_blank" rel="noopener noreferrer" data-testid={`lesson-${l.id}-sheet-link`} className="btn-primary">
                          <FileText size={14} /> Student sheet
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
